import type { MouseEventHandler } from 'react';
import styled, { css } from 'styled-components';
import type { TextStyleValue } from '@elfsight/configurator-contracts';
import { Show } from '@elfsight/lib-utils';
import { Button } from '@elfsight/widget-ui';
import { getInvertedColor } from '@elfsight/widget-utils';
import { STYLE_DEFAULTS } from '../../common';

export type ActionButtonProps = {
  actionText: string;
  onClick: MouseEventHandler<HTMLButtonElement>;
  disabledText?: string;
  disabled?: boolean;
  color?: string;
  font?: TextStyleValue;
  stretch?: boolean;
  maintainAppearanceWhenDisabled?: boolean;
};

export function ActionButton({
  actionText,
  onClick,
  disabled,
  disabledText,
  maintainAppearanceWhenDisabled = false,
  stretch = false,
  color = STYLE_DEFAULTS.accentColor,
  font = { ...STYLE_DEFAULTS, fontWeight: 700 }
}: ActionButtonProps) {
  const invertedColor = getInvertedColor(color);

  return (
    <StyledButton
      color={color}
      onClick={onClick}
      fontColor={invertedColor}
      font={font}
      borderWidth={0}
      borderRadius={8}
      verticalPadding={12}
      horizontalPadding={22}
      disabled={disabled}
      $stretch={stretch}
      $maintainAppearanceWhenDisabled={maintainAppearanceWhenDisabled}
    >
      <Show when={!disabled} fallback={disabledText ?? actionText}>
        {actionText}
      </Show>
    </StyledButton>
  );
}

const StyledButton = styled(Button)<{
  $stretch: boolean;
  $maintainAppearanceWhenDisabled: boolean;
}>`
  min-width: 200px;
  flex-shrink: 0;
  margin: 0 auto;

  ${({ $maintainAppearanceWhenDisabled }) =>
    $maintainAppearanceWhenDisabled &&
    css`
      opacity: 1;
    `}

  ${({ $stretch }) =>
    $stretch &&
    css`
      width: 100%;
    `}
`;
